/**
 * Admin test-persona and offering-preview API (task 42).
 *
 * Every call here is admin-only on the engine side (requireRole('admin')), with the sole
 * exception of stopImpersonation — while an impersonation is active the caller IS the
 * persona, so requiring admin would reject the very request that ends it. That endpoint
 * is de-escalation only: it clears one cookie and can do nothing else.
 *
 * Note what is NOT in this file: any notion of "what the persona can see". Impersonation
 * grants nothing. The engine resolves the session to the persona and every existing gate
 * then runs against that persona's own rows, so the portal simply makes ordinary calls
 * and gets whatever a real user in that state would get.
 */

import { api } from './apiClient';
import type { OfferingWithFinancials } from './offerings';

export interface Persona {
  userId: string;
  email: string;
  /** Names the gate state, not just the role — that is the useful thing on screen. */
  label: string;
  role: string;
  suspended: boolean;
  investorApproved: boolean | null;
  accredited: boolean | null;
  networkTagged: boolean;
  networkTaggedAt: string | null;
  createdAt: string;
}

export function listPersonas(): Promise<Persona[]> {
  return api.get<Persona[]>('/admin/personas');
}

export interface ImpersonationStart {
  persona: { userId: string; email: string; role: string; label: string };
  ttlMinutes: number;
}

/**
 * Begin acting as a persona.
 *
 * The engine sets a SEPARATE httpOnly cookie; the admin's own session cookies are never
 * written, so nothing here has to be stashed or restored. Callers should refresh identity
 * afterwards so the banner appears from the server's answer rather than an assumption.
 */
export function impersonate(personaUserId: string): Promise<ImpersonationStart> {
  return api.post<ImpersonationStart>(`/admin/personas/${personaUserId}/impersonate`);
}

export interface ImpersonationStop {
  stopped: boolean;
  returnedTo: string;
}

export function stopImpersonation(): Promise<ImpersonationStop> {
  return api.post<ImpersonationStop>('/admin/personas/stop');
}

// ── Offering preview ────────────────────────────────────────────────────────

/**
 * The admin's preview of a public deal page.
 *
 * Intersected with OfferingWithFinancials because an admin always passes the gate: the
 * engine builds this payload with showFinancials true unconditionally. Saying so in the
 * type means the preview does not have to re-check a gate it cannot fail, while the
 * public page still must.
 */
export interface OfferingPreview extends OfferingWithFinancials {
  preview: true;
  notLive: true;
  liveStatus: {
    status: string;
    offeringStartDate: string | null;
    eventStatus: string;
    /** Exactly why the public cannot see this yet. */
    reasonsNotPublic: string[];
  };
}

/**
 * The public deal page for an offering that is not public yet.
 *
 * Returns the identical payload the public route builds — same projection, same
 * disclosures, same simulator — plus the flags the view watermarks itself with. Reading
 * it publishes nothing: the offering stays absent from /api/opportunities/public.
 */
export function previewOffering(offeringId: string): Promise<OfferingPreview> {
  return api.get<OfferingPreview>(`/admin/opportunities/${offeringId}/preview`);
}

// ── 506(b) network tagging (task 43) ────────────────────────────────────────

export interface NetworkInvestor {
  userId: string;
  email: string;
  name: string | null;
  investorAccountId: string;
  approvedByAdmin: boolean;
  accreditedStatus: boolean;
  accreditationVerifiedAt: string | null;
  accreditationExpiresAt: string | null;
  networkTagged: boolean;
  /** The EARLIEST evaluation — when the relationship was established. */
  networkTaggedAt: string | null;
  evaluationCount: number;
}

export function listNetworkInvestors(taggedOnly = false): Promise<NetworkInvestor[]> {
  return api.get<NetworkInvestor[]>(
    `/admin/network/investors${taggedOnly ? '?taggedOnly=true' : ''}`,
  );
}

export interface NetworkEvaluation {
  id: string;
  investorId: string;
  evaluatedAt: string;
  evaluatedBy: { email: string; name: string | null };
  financialCircumstances: string;
  investmentSophistication: string;
  riskTolerance: string;
  investmentGoals: string;
}

export function listEvaluations(investorId: string): Promise<NetworkEvaluation[]> {
  return api.get<NetworkEvaluation[]>(`/admin/network/investors/${investorId}`);
}

/** The four fields. All required, all substantive — the engine enforces a length floor. */
export interface EvaluationInput {
  financialCircumstances: string;
  investmentSophistication: string;
  riskTolerance: string;
  investmentGoals: string;
}

/**
 * Record an evaluation, establishing the substantive relationship.
 *
 * This is a COMPLIANCE ACT, not a permission grant: the four fields are the record an
 * exemption rests on, and the engine rejects anything under 40 characters per field with
 * an error naming which one.
 */
export function tagInvestor(
  investorId: string,
  input: EvaluationInput,
): Promise<{ relationshipId: string; investorId: string; evaluatedAt: string }> {
  return api.post(`/admin/network/investors/${investorId}`, input);
}

export interface EligibleInvestor {
  userId: string;
  email: string;
  name: string | null;
  taggedAt: string | null;
  evaluationCount: number;
  isTestPersona: boolean;
}

export interface EligibilityReport {
  offering: {
    id: string;
    title: string;
    offeringType: string;
    status: string;
    offeringStartDate: string | null;
  };
  /** True when the raise has not opened — these are "if you opened it now" numbers. */
  projected: boolean;
  effectiveStart: string;
  eligible: EligibleInvestor[];
  /** Tagged, but after this raise began. Permanently excluded from it. */
  taggedTooLate: EligibleInvestor[];
  /** Approved but never evaluated. Still fixable — tag them BEFORE opening. */
  notTagged: EligibleInvestor[];
}

export function getEligibility(offeringId: string): Promise<EligibilityReport> {
  return api.get<EligibilityReport>(`/admin/opportunities/${offeringId}/eligible`);
}
